import type { FunctionComponent } from "react";
import { Fragment, memo, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { AppFooter } from "~/components/app-footer";
import { ArticleFooter } from "~/components/article/article-footer";
import { ArticlePaywallChecker } from "~/components/article/article-paywall-checker";
import { ArticlePaywallCheckerProvider } from "~/components/article/article-paywall-checker/contexts";
import { ArticleRender } from "~/components/article/article-render";
import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { ContentReadMoreContextProvider } from "~/components/content/content-read-more";
import { useFlag } from "~/lib/feature-flag";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import type { articleListArticle$key } from "~/queries/__generated__/articleListArticle.graphql";

import {
  useArticlePagination,
  useArticlePaginationChecker,
  useArticleRecommendation,
  useHandlePaginationNavigationHistory,
  useTrackPaginationCurrentArticle,
} from "./hooks";
import { Divider, Item, StyledLoading } from "./styles";
import { TopBanner } from "./top-banner";

type Props = {
  reference: articleListArticle$key;
};

export const ArticleList: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleListArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        ...topBannerContent
        ...articleFooterArticle
        ...articlePaginationCheckerArticle
        ...articlePaginationNavigationHistoryArticle @arguments(customContents: $customContents)
        ...articleRecommendationArticle
        ...articlePaywallCheckerContent
        ...articleRenderArticle @arguments(customContents: $customContents)
        ...helpersArticleSections
        ...contextsContentReadMoreContextProviderContent
      }
    `,
    reference_,
  );

  const { articleRecommendation } = useArticleRecommendation(article);
  const { data, hasNext, isLoading, loadNext } = useArticlePagination(
    articleRecommendation?.results.infiniteScrollArticleIds ?? [],
  );
  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage: hasNext,
    isLoading,
    onLoadMore: loadNext,
  });

  const items = useMemo(() => {
    type Item = typeof article;
    return [article, ...data] as [Item, ...Item[]];
  }, [article, data]);

  const { bindItemReference, currentArticle } = useTrackPaginationCurrentArticle(items);
  useHandlePaginationNavigationHistory(currentArticle, articleRecommendation);

  const { shouldStopPagination } = useArticlePaginationChecker(items);
  const enableInfiniteScroll = useFlag("enable_infinite_scroll");

  const shouldContinuePagination = enableInfiniteScroll && !shouldStopPagination && hasNext;

  return (
    <ArticlePaywallCheckerProvider>
      {items.map((item, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <>
              <Divider />
              <TopBanner reference={item} />
            </>
          )}
          <Item ref={bindItemReference(index)}>
            <ContentReadMoreContextProvider reference={item}>
              <MemorizedArticleRender reference={item} />
            </ContentReadMoreContextProvider>
          </Item>
          <ClientSideSuspense>
            <ArticlePaywallChecker reference={item} />
            {shouldContinuePagination && index === items.length - 1 && (
              <>
                <Divider />
                <StyledLoading ref={infiniteScrollTriggerReference} />
              </>
            )}
            {!shouldContinuePagination && index === items.length - 1 && (
              <>
                <ArticleFooter
                  articleRecommendation={{
                    articleIds: articleRecommendation.results.moreOnThisTopicWidgetIds,
                    recommendationId: articleRecommendation.recommendationId,
                  }}
                  divider={<Divider />}
                  reference={article}
                />
                <AppFooter variant={checkIsPostiesArticle(item) ? "posties" : undefined} />
              </>
            )}
          </ClientSideSuspense>
        </Fragment>
      ))}
    </ArticlePaywallCheckerProvider>
  );
};

const MemorizedArticleRender = memo(ArticleRender);

ArticleList.displayName = "ArticleList";
